// src/components/well3d/tileSession.js
//
// Well3D's tile session: the /tile/session call, its refresh timer, and the
// tile URL the Cesium imagery provider asks for. Moved out of Well3DSurface.jsx
// unchanged, so its tile-URL test can import it without a component file
// exporting non-components. The URL's path comes from api/tileUrl.js; its
// tile_token query parameter is Well3D's own and is filed separately.

import { fetchWithAuth } from '../../api/fetchWithAuth';
import { tilePath } from '../../api/tileUrl';

/** The session's options as the surface builds them. */
export function tileSessionOptions({ datasetKey, operatingAoi, projectId, onError }) {
  return {
    datasetKey,
    sessionBaseUrl: '/tile/session',
    operatingAoi,   // entitlement scope on tiles
    projectId,
    onError,
  };
}

/* =========================================================================
   Tile session manager
   ========================================================================= */
export class TileSessionManager {
  constructor({ datasetKey, sessionBaseUrl, operatingAoi, projectId, onError }) {
    this.datasetKey = datasetKey;
    this.sessionBaseUrl = sessionBaseUrl;
    this.operatingAoi = operatingAoi;
    this.projectId = projectId;
    this.onError = onError;
    this.token = null;
    this.cacheKey = null;
    this.expiresAt = 0;
    this.refreshTimer = null;
    this.ready = false;
  }
  async start() { await this.refresh(); }
  async refresh() {
    try {
      const qs = new URLSearchParams({ dataset: this.datasetKey });
      if (this.operatingAoi) qs.set('operating_aoi', this.operatingAoi);
      if (this.projectId)    qs.set('project_id',    this.projectId);
      const r = await fetchWithAuth(`${this.sessionBaseUrl}?${qs}`);
      if (!r.ok) throw new Error(`session ${r.status}`);
      const body = await r.json();
      const ttlMs = (body.expires_in ?? 300) * 1000;
      this.token = body.tile_token;
      this.cacheKey = body.tile_cache_key;
      this.expiresAt = Date.now() + ttlMs;
      this.ready = true;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), ttlMs * 0.8);
    } catch (e) {
      this.onError?.(e);
    }
  }
  tileUrl(z, x, y) {
    if (!this.token) return null;
    const qs = new URLSearchParams({ dataset: this.datasetKey, tile_token: this.token });
    if (this.cacheKey)     qs.set('ck',            this.cacheKey);
    if (this.operatingAoi) qs.set('operating_aoi', this.operatingAoi);
    return `${tilePath(z, x, y)}?${qs}`;
  }
  stop() { clearTimeout(this.refreshTimer); }
}
